/**
 * Manager Tour — the Manager's walk-through.
 * W66.X16 — in-platform guided tour.
 *
 * Role: Manager (the Manager)
 * Tag color: Gray (#6B7280)
 */

import type { GuidedTourStep } from "./types";

export const MANAGER_TOUR_STEPS: GuidedTourStep[] = [
  {
    stepNumber: 1,
    selector: '[data-tour="manager-team-summary"]',
    title: "Step 1 — Manager view: team movement summary",
    narration:
      "the Manager lands here Monday morning. The manager view shows not individual deals but the shape of the team: how many accounts moved forward this week, how many moved backward, how many had zero activity. This is the aggregate truth before any one-on-ones begin. the Manager does not need to ask each rep how their week was — the system has already answered that question with data.",
    coachingBeat:
      "System summary: 14 accounts moved forward. 3 accounts moved backward. 4 accounts had zero movement. The system has flagged 2 reps for coaching review. Monday call prep starts here.",
    action: "read",
    route: "/#/dashboards/manager",
  },
  {
    stepNumber: 2,
    selector: '[data-tour="manager-stack-rank"]',
    title: "Step 2 — Stack rank table",
    narration:
      "The stack rank table shows every rep on the Manager's team with three columns that matter: forward moves this week, backward moves this week, and days active. Not quota attainment — movement. Quota attainment is a lagging indicator. Movement is a leading indicator. A rep with high attainment but falling movement is a rep whose pipeline is deteriorating in real time. the Manager uses this table to intervene before the quarter ends, not after.",
    action: "read",
    route: "/#/dashboards/manager",
  },
  {
    stepNumber: 3,
    selector: '[data-tour="manager-zero-movement-rep"]',
    title: "Step 3 — Zero movement coaching prompt",
    narration:
      "the Manager clicks into the rep with zero forward movement this week. The system surfaces the coaching prompt immediately: this rep had no deals advance this week. The system does not editorialize — it shows the accounts, the stalled CPs, and the last touch date for each one. the Manager arrives at the one-on-one with the evidence already in hand. The conversation is about specific deals and specific actions, not general performance anxiety.",
    coachingBeat:
      "Coaching prompt: This rep had zero forward movement this week across 8 active accounts. The most likely cause: 3 accounts are waiting on CP5 (authority) answers that have been open for 14+ days. The coaching question is not 'why did nothing move' — it is 'what is blocking CP5 on these three accounts and what does the rep need from you to unlock it.'",
    action: "click",
    route: "/#/dashboards/manager",
  },
  {
    stepNumber: 4,
    selector: '[data-tour="manager-weekly-review"]',
    title: "Step 4 — Weekly review surface",
    narration:
      "The weekly review surface is the Friday digest preview: a structured snapshot of where each rep's pipeline stands at the close of the week. Not a report the rep writes — a report the system generates from the data the rep has entered. the Manager uses it to prepare for Friday calls, not to reconstruct what happened after the fact. The weekly review is the bridge between daily activity and quarterly forecast.",
    coachingBeat:
      "Friday digest preview ready: 3 reps improved their M-PACT scores this week. 1 rep's top account dropped by 8 points. The system has flagged that account for forced review in the weekly call.",
    action: "read",
    route: "/#/dashboards/manager",
  },
  {
    stepNumber: 5,
    selector: '[data-tour="manager-override-note"]',
    title: "Step 5 — Override and coaching note capture",
    narration:
      "When the Manager has information the system does not — a verbal commitment from a buyer, a relationship insight from a previous engagement, context that never makes it into a CP answer — she can enter a coaching note or a manager override. This is not a way to game the score. It is a way to put judgment on record. Every override is dated, attributed, and visible to Bryan K. Short and the team. If the override proves wrong, the record shows it. If it proves right, it is evidence of good coaching.",
    action: "click",
    route: "/#/dashboards/manager",
  },
  {
    stepNumber: 6,
    selector: '[data-tour="manager-drill-down"]',
    title: "Step 6 — Drill-down: rep to account to stalled CP",
    narration:
      "From the stack rank, the Manager can drill all the way from the rep view to a specific account to the exact CP that is stalled. Three clicks from the team summary to the blocking question on a specific deal. The manager does not have to reconstruct the pipeline in her head or ask the rep to pull up their notes. The system surfaces the specific checkpoint, the last answer, when it was entered, and what question remains unanswered. That is the coaching surface — the specific, documented gap that the rep and manager work on together.",
    coachingBeat:
      "Drill-down result: Rep shows zero movement on Meridian Partners. Root cause: CP6 — Financial Sign-off — has been in 'in progress' state for 22 days. The buyer has not identified a financial signer. The coaching prompt: the rep needs to escalate inside Meridian. the Manager can provide a suggested approach in the coaching note.",
    action: "click",
    route: "/#/dashboards/manager",
  },
];
